import {
    Avantage,
    Contrat,
    ImpactSimulationResponse,
    OffresSecondaire,
    OpApplicable,
    RecapitulatifPU,
} from '@app-types/api/mcube';
import {
    GalliumData,
    GalliumDataStore,
    GalliumOffer,
    GalliumOffreApi,
    GalliumPrice,
    GalliumPriceMap,
    GalliumStore,
    HeldOptionApi,
    HeldPlanApi,
    ProposedOptionApi,
    ProposedPlanApi,
    SimulationDataApi,
} from '@app-types/gallium';
import {
    ACTION_TYPES,
    ApplicableOp,
    Contract,
    ImpactsSimulation,
    SecondaryOffer,
    SummaryPU,
} from '@app-types/simulator/simulator';

import { PlanSubTypeId } from '@constants/plan';

export class MCubeMapper {
    static mapToImpactsSimulation(apiData: ImpactSimulationResponse): ImpactsSimulation {
        return {
            contracts: apiData.contrats?.map(MCubeMapper.mapContract),
            currentCommercialOrders: apiData?.commandesCommercialesEnCours?.map((order) => ({
                mainOffers: order.offresPrincipales.map(MCubeMapper.mapApplicableOp),
                orderId: order.idCommande,
            })),
            messages: apiData?.messages?.map((message) => ({
                code: message.code,
                label: message.libelle,
                parameters: message?.parametres?.map((param) => ({
                    code: param.code,
                    value: param.valeur,
                })),
            })),
            proposed: apiData?.proposees?.map((offer) => ({
                secondaryOffers: offer.offresSecondaires?.map(MCubeMapper.mapSecondaryOffer),
                journey: offer.parcours,
                applicableOp: MCubeMapper.mapApplicableOp(offer.opApplicable),
            })),
            summaryPU: MCubeMapper.mapToSummary(apiData.recapitulatifPU),
        };
    }

    static mapContract(apiData: Contrat): Contract {
        return {
            id: apiData.idContrat,
            msisdn: apiData.msisdn,
            voipNumber: apiData.numeroVoip,
            applicableOp: MCubeMapper.mapApplicableOp(apiData.opApplicable),
            secondaryOffers: apiData.offresSecondaires?.map(MCubeMapper.mapSecondaryOffer),
        };
    }

    static mapApplicableOp(apiData: OpApplicable): ApplicableOp {
        return {
            advantages: apiData.avantages?.map(MCubeMapper.mapAdvantage),
            compatibleWithDiscounts: apiData.compatibleAvecRemises,
            conditionForDiscounts: apiData.conditionPourRemises,
            endOfCommitmentDate: apiData.dateFinEngagement,
            commitmentDuration: apiData.dureeEngagement,
            convergenceRange: apiData.gammeConvergence,
            offerId: apiData.idOffre,
            commercialLabel: apiData.libelleCommercial,
            marketLine: apiData.ligneMarche,
            convergentMarketLine: apiData.ligneMarcheConvergent,
            amountPaid: apiData.montantPaye,
            amountPaidAfterSimulation: apiData.montantPayeApresSimulation,
            convergenceRangeLevel: apiData.niveauGammeConvergent,
            options: apiData.options?.map((option) => ({
                label: option.libelle,
                amountIncludingTax: option.montantTTC,
                vatRate: option.tauxTVA,
            })),
            priceIncludingTax: apiData.tarifTTC,
        };
    }

    static mapAdvantage(apiData: Avantage) {
        return {
            action: apiData.action as ACTION_TYPES,
            discountIncludingTax: apiData.remiseTTC,
            duration: apiData.duree,
            durationUnit: apiData.uniteDuree,
            label: apiData.libelle,
            endOfValidityDate: apiData.dateFinValidite,
            familyPack: apiData.packFamille,
            offerId: apiData.idOffre,
            vatRate: apiData.tauxTVA,
        };
    }

    static mapSecondaryOffer(apiData: OffresSecondaire): SecondaryOffer {
        return {
            action: apiData.action,
            offerId: apiData.idOffre,
            commercialLabel: apiData.libelleCommercial,
            priceExcludingTax: apiData.tarifHT,
            priceIncludingTax: apiData.tarifTTC,
            vatRate: apiData.tauxTVA,
            incompatibleType: apiData.typeIncompatible,
            offerType: apiData.typeOffre,
        };
    }

    static mapToSummary(recapitulatifPU: RecapitulatifPU): SummaryPU {
        return {
            currentPlan: {
                mainOffers: {
                    globalAmount: recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobal,
                    globalAmountCurrentOrders:
                        recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobalCommandesEncours,
                    globalAmountHeldOffers: recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobalOffresDetenues,
                    globalAmountProposed: recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobalPropose,
                },
                toutatisDiscounts: {
                    globalAmount: recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobal,
                    globalAmountCurrentOrders:
                        recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobalPourCommandesEncours,
                    globalAmountHeldOffers:
                        recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobalPourOffresDetenues,
                    globalAmountProposed: recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobalPropose,
                },
            },
            simulationImpact: {
                mainOffers: {
                    globalAmount: recapitulatifPU.impactSimulation.offresPrincipales.montantGlobal,
                    globalAmountCurrentOrders:
                        recapitulatifPU.impactSimulation.offresPrincipales.montantGlobalCommandesEncours,
                    globalAmountHeldOffers:
                        recapitulatifPU.impactSimulation.offresPrincipales.montantGlobalOffresDetenues,
                    globalAmountProposed: recapitulatifPU.impactSimulation.offresPrincipales.montantGlobalPropose,
                },
                toutatisDiscounts: {
                    globalAmount: recapitulatifPU.impactSimulation.remisesToutatis.montantGlobal,
                    globalAmountCurrentOrders:
                        recapitulatifPU.impactSimulation.remisesToutatis.montantGlobalPourCommandesEncours,
                    globalAmountHeldOffers:
                        recapitulatifPU.impactSimulation.remisesToutatis.montantGlobalPourOffresDetenues,
                    globalAmountProposed: recapitulatifPU.impactSimulation.remisesToutatis.montantGlobalPropose,
                },
            },
        };
    }

    static mapToGalliumData(apiData: SimulationDataApi): GalliumData {
        const sortedSimulations = [...apiData.simulations].sort(
            (a, b) =>
                MCubeMapper.parseDataEnvelope(a.offrePrincipaleCible.enveloppeDataReconstitue) -
                MCubeMapper.parseDataEnvelope(b.offrePrincipaleCible.enveloppeDataReconstitue),
        );

        const offerIds = sortedSimulations.map((sim) => sim.offrePrincipaleCible.idOffre);
        const sourceOffer = MCubeMapper.mapHeldPlanToGalliumOffer(
            apiData.offrePrincipaleDetenue,
            apiData.offresSecondairesDetenues,
        );
        const simulations = sortedSimulations.map((sim) =>
            MCubeMapper.mapProposedPlanToGalliumOffer(sim.offrePrincipaleCible, sim.offresSecondairesCibles),
        );

        return { offerIds, sourceOffer, simulations };
    }

    private static parseDataEnvelope(envelope?: string): number {
        if (!envelope) return 0;

        // Regex pour capturer les valeurs avec unités (Mo, Go, etc.)
        const dataPattern = /(\d+(?:\.\d+)?)\s*(Mo|Go|To)/gi;
        const matches = envelope.matchAll(dataPattern);

        return Array.from(matches).reduce((acc, match) => {
            const value = Number.parseFloat(match[1] ?? '0');
            const unit = match[2]?.toLowerCase() ?? '';

            switch (unit) {
                case 'mo':
                    return acc + value;
                case 'go':
                    return acc + value * 1024;
                case 'to':
                    return acc + value * 1024 * 1024;
                default:
                    return acc;
            }
        }, 0);
    }

    private static mapHeldPlanToGalliumOffer(plan: HeldPlanApi, options: HeldOptionApi[]): GalliumOffer {
        const baseOffer = MCubeMapper.mapPlanToGallium(plan);
        const advantageDiscounts = options
            ?.filter((os) => os.typeOffre === 'AVANTAGE')
            ?.map((os) => MCubeMapper.mapOptionToGallium(os));

        return { ...baseOffer, advantageDiscounts };
    }

    private static mapProposedPlanToGalliumOffer(plan: ProposedPlanApi, options: ProposedOptionApi[]): GalliumOffer {
        const baseOffer = MCubeMapper.mapPlanToGallium(plan);
        const advantageDiscounts = options
            ?.filter((os) => os.typeOffre === 'AVANTAGE' && os.action !== ACTION_TYPES.RESILIATION)
            ?.map((os) => MCubeMapper.mapOptionToGallium(os));

        return { ...baseOffer, advantageDiscounts };
    }

    private static mapPlanToGallium(plan: HeldPlanApi | ProposedPlanApi): GalliumOffer {
        return {
            offerId: plan.idOffre,
            subType: plan.sousTypeCommercial as PlanSubTypeId | undefined,
            commercialLabel: plan.libelleCommercial,
            systemLabel: plan.libelleCommercial,
            salesFamilies: plan.famillesVente?.map((sf) => ({
                saleFamilyId: sf.idFamilleVente,
                saleFamilyLabel: sf.libelleFamilleVente,
            })),
            recurringPrice: {
                priceExcludingTax: plan.tarifHT,
                priceIncludingTax: plan.tarifTTC,
                vatRate: plan.tauxTVA,
            },
            discountedPrice: {
                priceIncludingTax: plan.tarifRemiseTTC,
                discountAmount: plan.montantRemiseTTC,
            },
        };
    }

    private static mapOptionToGallium(option: HeldOptionApi | ProposedOptionApi): GalliumOffer {
        const duration = option.dureeApplication?.duree
            ? Number.parseInt(option.dureeApplication.duree, 10)
            : undefined;
        return {
            offerId: option.idOffre,
            commercialLabel: option.libelleCommercial,
            systemLabel: option.libelleCommercial,
            salesFamilies: option.famillesVente?.map((sf) => ({
                saleFamilyId: sf.idFamilleVente,
                saleFamilyLabel: sf.libelleFamilleVente,
            })),
            recurringPrice: {
                priceExcludingTax: option.tarifHT,
                priceIncludingTax: option.tarifTTC,
                vatRate: option.tauxTVA,
            },
            duration,
            durationUnit: option.dureeApplication?.uniteDuree ?? '',
            validityEndDate: option.dateFinValidite,
        };
    }

    static mapToStorageGalliumPrices(data: GalliumData): GalliumPriceMap | undefined {
        if (!data.sourceOffer?.offerId) {
            return undefined;
        }

        const prices: GalliumPriceMap = {};

        const sourcePrice = MCubeMapper.mapToGalliumPrice(data.sourceOffer);
        if (sourcePrice) {
            prices[data.sourceOffer.offerId] = sourcePrice;
        }

        for (const offer of data.simulations) {
            if (offer.messages?.[0]?.type === 'ERROR') continue;

            const price = MCubeMapper.mapToGalliumPrice(offer);
            if (price && offer.offerId) {
                prices[offer.offerId] = price;
            }
        }

        return prices;
    }

    static mapToStorageGallium(data: GalliumData, contractId: string): GalliumStore {
        const mappedData: GalliumDataStore = {
            ops: data.offerIds,
            opSource: MCubeMapper.mapGalliumOfferToApi(data.sourceOffer),
            simulation: data.simulations.map(MCubeMapper.mapGalliumOfferToApi),
        };
        return { [contractId]: mappedData };
    }

    private static mapGalliumOfferToApi(offer: GalliumOffer): GalliumOffreApi {
        return {
            idOffre: offer.offerId,
            sousTypeCommercial: offer.subType,
            libelleCommercial: offer.commercialLabel,
            libelleSysteme: offer.systemLabel,
            familleVente: offer.salesFamilies?.map((sf) => ({
                idFamilleVente: sf.saleFamilyId,
                libelleFamilleVente: sf.saleFamilyLabel,
            })),
            tarifRecurrent: offer.recurringPrice
                ? {
                      tarifHT: offer.recurringPrice.priceExcludingTax,
                      tarifTTC: offer.recurringPrice.priceIncludingTax,
                      tauxTVA: offer.recurringPrice.vatRate,
                  }
                : undefined,
            tarifMoinsRemise: offer.discountedPrice
                ? {
                      tarifTTC: offer.discountedPrice.priceIncludingTax,
                      montantRemiseTTC: offer.discountedPrice.discountAmount,
                  }
                : undefined,
            dureeOffre: offer.duration,
            uniteDureeOffre: offer.durationUnit,
            dateFinValidite: offer.validityEndDate,
            remiseAvantage: offer.advantageDiscounts?.map(MCubeMapper.mapGalliumOfferToApi),
            message: offer.messages,
        };
    }

    static mapToGalliumPrice(offer: GalliumOffer): GalliumPrice | null {
        if (offer.messages?.[0]?.type === 'ERROR') {
            return null;
        }

        const price: GalliumPrice = {
            price: offer.recurringPrice?.priceIncludingTax,
        };

        const hasDiscount =
            offer.discountedPrice?.discountAmount &&
            offer.discountedPrice.discountAmount < 0 &&
            offer.recurringPrice?.priceIncludingTax !== offer.discountedPrice.priceIncludingTax;

        if (!hasDiscount) {
            return price;
        }

        price.promoPrice = offer.discountedPrice!.priceIncludingTax;
        price.refund = offer.discountedPrice!.discountAmount;

        if (!offer.advantageDiscounts?.length) {
            return price;
        }

        let refundsWithDate = 0;

        price.refundsList = offer.advantageDiscounts.map((discount) => {
            if (discount.duration) {
                refundsWithDate++;
                price.promoLabel = `pendant ${discount.duration} ${discount.durationUnit?.toLowerCase()} `;
            }

            return {
                untilDate: discount.validityEndDate,
                name: discount.commercialLabel,
                amount: discount.recurringPrice?.priceIncludingTax ?? 0,
                duration: discount.duration ?? 0,
            };
        });

        price.refundsWithDate = refundsWithDate;

        return price;
    }
}
